import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { adminAPI, departmentAPI } from "../services/api";
import { toast } from "sonner";
import {
  Users,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Phone,
  Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

interface User {
  id: string;
  fullName: string;
  email: string;
  role: "ADMIN" | "TECHNICIAN" | "STUDENT";
  departmentId: string | null;
  department?: { id: string; name: string };
  phoneNumber: string | null;
  isActive: boolean;
  createdAt: string;
  lastLogin: string | null;
  organizationId?: string;
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "STUDENT" as "ADMIN" | "TECHNICIAN" | "STUDENT",
    departmentId: "",
    phoneNumber: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const usersResponse = await adminAPI.getUsers();
      if (usersResponse.success || Array.isArray(usersResponse)) {
        const usersList = Array.isArray(usersResponse)
          ? usersResponse
          : usersResponse.data?.users ||
            usersResponse.data?.items ||
            usersResponse.data ||
            [];
        setUsers(Array.isArray(usersList) ? usersList : []);
      } else {
        setUsers([]);
      }

      const deptsResponse = await departmentAPI.list();
      if (deptsResponse.success || Array.isArray(deptsResponse)) {
        const deptsList = Array.isArray(deptsResponse)
          ? deptsResponse
          : deptsResponse.data?.items || deptsResponse.data || [];
        setDepartments(Array.isArray(deptsList) ? deptsList : []);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      toast.error("Failed to load user data");
      setUsers([]);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedUser(null);
    setFormData({
      fullName: "",
      email: "",
      password: "",
      role: "STUDENT",
      departmentId: "",
      phoneNumber: "",
    });
    setShowDialog(true);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName,
      email: user.email,
      password: "",
      role: user.role,
      departmentId: user.departmentId || "",
      phoneNumber: user.phoneNumber || "",
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedUser) {
        const response = await adminAPI.updateUser(selectedUser.id, {
          fullName: formData.fullName,
          email: formData.email,
          role: formData.role,
          departmentId: formData.departmentId || null,
          phoneNumber: formData.phoneNumber || null,
          ...(formData.password && { password: formData.password }),
        });

        if (response.success) {
          toast.success("User updated successfully");
          loadData();
          setShowDialog(false);
        }
      } else {
        if (!formData.password) {
          toast.error("Password is required for new users");
          return;
        }

        const response = await adminAPI.createUser({
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          departmentId: formData.departmentId || null,
          phoneNumber: formData.phoneNumber || null,
          organizationId: currentUser?.organizationId,
        });

        if (response.success) {
          toast.success("User created successfully");
          loadData();
          setShowDialog(false);
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to save user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await adminAPI.deleteUser(userToDelete.id);
      toast.success("User deleted successfully");
      loadData();
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredUsers = Array.isArray(users)
    ? users.filter((user) => {
        const matchesSearch =
          (user.fullName || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (user.email || "").toLowerCase().includes(searchQuery.toLowerCase());
        const matchesRole = roleFilter === "all" || user.role === roleFilter;
        return matchesSearch && matchesRole;
      })
    : [];

  const roleColors = {
    ADMIN: "bg-red-500/10 text-red-400 border-red-500/30",
    TECHNICIAN: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    STUDENT: "bg-green-500/10 text-green-400 border-green-500/30",
  };

  const stats = {
    total: Array.isArray(users) ? users.length : 0,
    admins: Array.isArray(users)
      ? users.filter((u) => u.role === "ADMIN").length
      : 0,
    technicians: Array.isArray(users)
      ? users.filter((u) => u.role === "TECHNICIAN").length
      : 0,
    students: Array.isArray(users)
      ? users.filter((u) => u.role === "STUDENT").length
      : 0,
  };

  return (
    <DashboardLayout>
      <div className="min-h-full p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
              User Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage system users, roles, and permissions
            </p>
          </motion.div>

          <button
            onClick={openCreateDialog}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all hover:scale-105 shadow-lg cursor-pointer"
          >
            <Plus className="w-5 h-5" />
            Add User
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Users", value: stats.total, color: "text-primary" },
            {
              label: "Administrators",
              value: stats.admins,
              color: "text-red-400",
            },
            {
              label: "Technicians",
              value: stats.technicians,
              color: "text-blue-400",
            },
            {
              label: "Students",
              value: stats.students,
              color: "text-green-400",
            },
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
            >
              <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.color} font-mono`}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-xl p-4 shadow-lg"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
            >
              <option value="all">All Roles</option>
              <option value="ADMIN">Administrators</option>
              <option value="TECHNICIAN">Technicians</option>
              <option value="STUDENT">Students</option>
            </select>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/50 rounded-xl overflow-hidden shadow-lg"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border/50 bg-muted/30">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                      User
                    </th>
                    <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                      Role
                    </th>
                    <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                      Department
                    </th>
                    <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                      Contact
                    </th>
                    <th className="text-left p-4 text-sm font-semibold text-muted-foreground">
                      Status
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-border/30 hover:bg-muted/20 transition-colors"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-foreground">
                            {user.fullName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded border ${
                            roleColors[user.role as keyof typeof roleColors]
                          }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.department?.name || "N/A"}
                      </td>
                      <td className="p-4">
                        <div className="text-xs text-muted-foreground space-y-1">
                          {user.phoneNumber && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {user.phoneNumber}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded ${
                            user.isActive
                              ? "bg-green-500/10 text-green-400"
                              : "bg-gray-500/10 text-gray-400"
                          }`}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditDialog(user)}
                            className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors cursor-pointer"
                            title="Edit user"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            className="p-2 hover:bg-red-500/10 text-red-400 rounded-lg transition-colors cursor-pointer"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {showDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-border/50 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-foreground">
                  {selectedUser ? "Edit User" : "Create New User"}
                </h3>
                <button
                  onClick={() => setShowDialog(false)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      setFormData({ ...formData, fullName: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Password {!selectedUser && "*"}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required={!selectedUser}
                    placeholder={
                      selectedUser ? "Leave blank to keep current" : ""
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        role: e.target.value as
                          | "ADMIN"
                          | "TECHNICIAN"
                          | "STUDENT",
                      })
                    }
                    required
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  >
                    <option value="STUDENT">Student</option>
                    <option value="TECHNICIAN">Technician</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) =>
                      setFormData({ ...formData, departmentId: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  >
                    <option value="">No Department</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      setFormData({ ...formData, phoneNumber: e.target.value })
                    }
                    className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        {selectedUser ? "Update" : "Create"} User
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showDeleteDialog && userToDelete && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-border/50 rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-500/10 rounded-full">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Delete User
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-foreground mb-2">
                  Are you sure you want to delete this user?
                </p>
                <div className="bg-muted/30 rounded-lg p-3 border border-border/30">
                  <p className="font-semibold text-red-400">
                    {userToDelete.fullName}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {userToDelete.email}
                  </p>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-medium rounded border mt-2 ${
                      roleColors[userToDelete.role as keyof typeof roleColors]
                    }`}
                  >
                    {userToDelete.role}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  All user data will be permanently removed from the system.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setUserToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all font-medium cursor-pointer disabled:opacity-50"
                >
                  {isDeleting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
